// skipcq: JS-W1028, JS-0128
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, TableCell, Tooltip, Typography } from '@mui/material';

export const clipboardCellInnerSx = {
  display: 'block',
  width: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export async function copyTextToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      /* fall through to legacy copy */
    }
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
}

export function ClipboardCopyTableCell({ value, sx }) {
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef(null);
  const textToCopy = value == null || value === '' ? '' : String(value);
  const canCopy = textToCopy !== '';
  const display = canCopy ? textToCopy : '—';

  useEffect(
    function clearCopyTimeoutOnUnmount() {
      return function cleanupCopyTimeout() {
        if (copyTimeoutRef.current != null) {
          window.clearTimeout(copyTimeoutRef.current);
        }
      };
    },
    [],
  );

  const handleClick = useCallback(
    async function handleClick(event) {
      event.stopPropagation();
      if (!canCopy) {
        return;
      }
      const copiedOk = await copyTextToClipboard(textToCopy);
      if (!copiedOk) {
        console.warn('Unable to copy to clipboard');
        return;
      }
      if (copyTimeoutRef.current != null) {
        window.clearTimeout(copyTimeoutRef.current);
      }
      setCopied(true);
      copyTimeoutRef.current = window.setTimeout(function resetCopiedFlag() {
        setCopied(false);
        copyTimeoutRef.current = null;
      }, 2000);
    },
    [canCopy, textToCopy],
  );

  const handleKeyDown = useCallback(
    function handleKeyDown(event) {
      if (!canCopy) {
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleClick(event).catch(function clipboardCopyKeyError() {
          /* errors surfaced in handleClick */
        });
      }
    },
    [canCopy, handleClick],
  );

  const tooltipTitle = !canCopy
    ? ''
    : copied
      ? 'Copied'
      : (
          <span>
            Click to copy
            <br />
            <Typography component="span" variant="caption" sx={{ fontFamily: 'monospace' }}>
              {textToCopy}
            </Typography>
          </span>
        );

  return (
    <TableCell sx={sx}>
      <Tooltip
        title={tooltipTitle}
        disableHoverListener={!canCopy}
        disableFocusListener={!canCopy}
        disableTouchListener={!canCopy}
      >
        <Box
          component="span"
          onClick={canCopy ? handleClick : undefined}
          onKeyDown={canCopy ? handleKeyDown : undefined}
          role={canCopy ? 'button' : undefined}
          tabIndex={canCopy ? 0 : undefined}
          sx={{
            ...clipboardCellInnerSx,
            cursor: canCopy ? 'pointer' : 'default',
          }}
        >
          {display}
        </Box>
      </Tooltip>
    </TableCell>
  );
}
